/**
 * Kirundi catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Kirundi (Ikirundi) at a mathematician's register. Kirundi and Kinyarwanda are
 * closely related and largely mutually intelligible; this catalog tracks the Kinyarwanda
 * rendering and is flagged "expert-review suggested" in `Agent9_Batch3_REPORT.md` for a native
 * Kirundi pass. **Warrant → «Igihamya»** (proof/warrant — flagged). Scientific loanwords are
 * used where Kirundi standardly borrows. W/M grades stay Latin (`::numbering-system/latn`).
 * CLDR cardinal categories are one / other (umupaka → imipaka). Per INV-STABLE-ID a wrong
 * translation is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const RN_CATALOG = {
    locale: 'rn',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Iperereza',
        [P2.sectionClaims]: 'Ivyivugwa',
        [P2.sectionIntegrity]: 'Ubudahemuka',
        [P2.axisWarrantLabel]: 'Igihamya',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Vya empiriki',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Insobanuro} interp {Ubusobanuro} testable {Bishobora kugeragezwa} tested {Vyaragejwe} refuted {Vyahakanywe} program {Gahunda y’ubushakashatsi} philos {Vy’ifilozofiya} other {Ntibizwi}}',
        [P2.axisModalLabel]: 'Modali',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Urugero rw’inkomezi {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Bishobora gutohozwa',
        [P2.stateRefuted]: 'Vyahakanywe',
        [P2.stateOutside]: 'Hanze y’urwego',
        [P2.verdictConflict]: 'Bivuguruzanya na {constraints}',
        [P2.verdictOutside]: 'Bisaba {missing}',
        [P2.lossRegisterPlain]: 'Vyoroshejwe mu rurimi rusanzwe — bimwe mu kuri vyasibwe.',
        [P2.lossRegisterKid]: 'Vyoroshejwe ku basomyi batoyi — ibisobanuro vyinshi vyasibwe.',
        [P2.lossProfileDetail]: 'Umwirondoro wuzuye wa Igihamya / Vya empiriki / Modali wagabanijwe ng’aha.',
        [P2.lossConflictDetail]: 'Ibisobanuro vy’amatati vyagabanijwe muri iyi shusho.',
        [P2.laneVerified]: 'Vyemejwe',
        [P2.laneUnverified]: 'Ntivyemejwe',
        [P2.laneConflict]: 'Amatati yagaragajwe',
        [P2.constraintLogic]: 'Lojiki',
        [P2.constraintLightBends]: 'Umuco ugororoka mu murwi w’inkomezi zikwega',
        [P2.constraintParallelPostulate]: 'Postula y’imirongo ingana',
        [P2.claimScalarGravity]: 'Inyigisho ya skalari y’inkomezi zikwega',
        [P2.claimNonEuclidean]: 'Jewometiri itari iya Euclide',
        [P2.claimSubstrateSr]: 'Ubusobanuro bwa substrate bw’isano ridasanzwe',
        [P2.edgeExtObservation]: 'Kwitegereza hanze',
        [P2.edgeResourceSample]: 'Umutungo wo kubara usangiwe',
        [P2.edgeHeuristicSample]: 'Ubuyobozi bwa heuristic',
        [P2.edgeSharedSample]: 'Urufatiro rusangiwe',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Imipaka',
        [P2.sectionEdges]: 'Inkombe',
        [P2.sectionFrame]: 'Ikadiri',
        [P2.sectionExposure]: 'Kugaragazwa',
        [P2.statusAdoptedFormal]: 'Vyemejwe (ku mugaragaro)',
        [P2.statusAdoptedInterpretive]: 'Vyemejwe (mu busobanuro)',
        [P2.statusSuspended]: 'Vyahagaritswe',
        [P2.edgeEffectConstraint]: 'Bigize umupaka (bishobora guhakana)',
        [P2.edgeEffectResource]: 'Umutungo — nta co bikora ku ngingo',
        [P2.edgeEffectHeuristic]: 'Heuristic — nta co bikora ku ngingo',
        [P2.edgeEffectShared]: 'Urufatiro rusangiwe — nta co bikora ku ngingo',
        [P2.frameActive]: '{count, plural, =0 {Nta mipaka ikora} one {# umupaka ukora} other {# imipaka ikora}}',
        [P2.exposureNone]: 'Nta kwishimikiza ku busobanuro',
        [P2.exposureDep]: 'Bishimikiye kuri {id}',
        [P2.laneTrackMachine]: 'Imashini',
        [P2.laneTrackExpert]: 'Impuguke',
        [P2.laneTrackCommunity]: 'Umuryango',
        [P2.expertAudited]: 'Vyasuzumwe',
        [P2.expertInProgress]: 'Biragenda',
        [P2.expertNotReviewed]: 'Ntivyasubiwemwo',
        [P2.communityFlags]: 'Hari ibimenyetso',
        [P2.communityNone]: 'Nta bimenyetso',
        [P2.controlChallenge]: 'Hakana',
        [P2.controlActivate]: 'Tangiza',
        [P2.controlMakeInterpretive]: 'Gira ubusobanuro',
        [P2.controlImportEdge]: 'Injiza inkombe',
        [P2.controlPromote]: 'Duza',
        [P2.controlUndo]: 'Garura',
        [P2.controlRedo]: 'Subira ukore',
        [P2.controlRegister]: 'Iyandikishe',
        [P2.promoteRejected]: 'Kuduza vyanzwe',
        [P2.promoteAccepted]: 'Kuduza vyemewe',
        [P2.promoteReasonRefutationTerminal]: 'Igihamya ntigishobora gukiza guhakanwa.',
        [P2.promoteReasonKeystone]: 'Kuduza ngombwa bisaba inkunga ya empiriki.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Ubwoko bwa empiriki ntibwashizweho.',
        [P2.promoteReasonModalNotFixed]: 'Urwego rwa modali ntirwashizweho.',
        [P2.promoteReasonWarrantNotImproved]: 'Urwego rw’igihamya ntirwateye imbere.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=rn.js.map